"use client";

import { useEffect, useMemo, useState } from "react";
import { addDays, format } from "date-fns";
import { pl } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Sheet } from "@/components/shell/Sheet";
import { Button } from "@/components/ui/Button";
import { Mascot } from "@/components/brand/Mascot";
import { useAuth } from "@/lib/auth/context";
import { useChildMembers } from "@/lib/hooks/useMembers";
import { startOfThisWeek } from "@/lib/utils/dates";
import { joinChildrenNames, useSundayPlan, type PlanDay } from "@/lib/hooks/useSundayPlan";
import { QuickAddChoreSheet } from "./QuickAddChoreSheet";
import { assignedToLabel, CHORE_ASSIGN_ANY, CHORE_ASSIGN_ALL_CHILDREN } from "@/lib/utils/chores";
import type { Chore, Member } from "@/lib/schemas";

type Props = {
  open: boolean;
  onClose: () => void;
};

const AVATAR_TONE: Record<Member["avatarColor"], string> = {
  rose: "bg-rose-500",
  amber: "bg-amber-500",
  emerald: "bg-emerald-500",
  sky: "bg-sky-500",
  violet: "bg-violet-500",
  fuchsia: "bg-fuchsia-500",
};

export function SundayPlanSheet({ open, onClose }: Props) {
  const { userDoc } = useAuth();
  // Dziecko nie układa kalendarza w aplikacji — to rytuał z rodzicem
  // przy stole. Pokazujemy zachętę z maskotką, bez UI rodzica.
  if (userDoc?.role === "child") {
    return <SundayPlanChildView open={open} onClose={onClose} />;
  }
  return <SundayPlanParentView open={open} onClose={onClose} />;
}

function SundayPlanChildView({ open, onClose }: Props) {
  return (
    <Sheet
      open={open}
      onClose={onClose}
      footer={
        <Button variant="primary" block onClick={onClose}>
          Dobra, idę pogadać
        </Button>
      }
    >
      <div className="flex flex-col items-center text-center pt-2 pb-2 gap-3">
        <Mascot emotion="curious" size={120} shadow />
        <h2
          className="font-display text-[20px] font-semibold text-navy"
          style={{ letterSpacing: "-0.025em" }}
        >
          Plan tygodnia układacie razem
        </h2>
        <p className="text-[13px] text-muted leading-relaxed max-w-[280px]">
          Pokaż mamie albo tacie swoje pomysły z zakładki{" "}
          <span className="font-semibold text-navy">Zadania</span>. Wspólnie
          rozłożycie zadania po dniach tygodnia — przy stole, nie w telefonie.
        </p>
        <div
          className="rounded-[14px] border border-hay-bd px-4 py-3 mt-1 w-full"
          style={{
            backgroundImage:
              "linear-gradient(135deg, rgba(255,201,60,0.18), rgba(255,201,60,0.05))",
          }}
        >
          <div
            className="text-[11px] font-bold uppercase tracking-[0.05em]"
            style={{ color: "#7A5A00" }}
          >
            💡 Jeszcze bez pomysłu?
          </div>
          <div className="text-[12px] text-navy mt-1 leading-relaxed">
            Wejdź w <span className="font-semibold">Zadania</span> i kliknij
            „Zaproponuj zadanie" — rodzic to zobaczy, gdy będziecie planować.
          </div>
        </div>
      </div>
    </Sheet>
  );
}

function SundayPlanParentView({ open, onClose }: Props) {
  const navigate = useNavigate();
  const { userDoc } = useAuth();
  const hid = userDoc?.householdId;

  // Offset tygodnia: 0 = bieżący, +1 = następny, -1 = poprzedni.
  const [weekOffset, setWeekOffset] = useState(0);
  const weekStart = useMemo(
    () => addDays(startOfThisWeek(), weekOffset * 7),
    [weekOffset],
  );
  const plan = useSundayPlan(hid, weekStart);

  // Wybrany dzień do expand panel (null = żaden). Klik tego samego = toggle off.
  const [selectedDayIndex, setSelectedDayIndex] = useState<number | null>(null);
  // Sub-sheet do dodawania chore na konkretny dzień.
  const [quickAddDate, setQuickAddDate] = useState<Date | null>(null);

  // Reset wyboru dnia przy zmianie tygodnia — dzień z poprzedniego tygodnia
  // jest nieaktualny (inna data, inna lista chores).
  useEffect(() => {
    setSelectedDayIndex(null);
  }, [weekOffset]);

  const { members: allChildren } = useChildMembers(hid);

  const goToApprovals = () => {
    onClose();
    navigate("/approvals");
  };

  const childNames = joinChildrenNames(plan.children);
  const childByUid = new Map(plan.children.map((m) => [m.userId, m]));

  const selectedDay =
    selectedDayIndex !== null ? plan.days[selectedDayIndex] : null;

  const toggleDay = (i: number) => {
    if (plan.days[i]?.isPast) return; // przeszłość nie do planowania
    setSelectedDayIndex((cur) => (cur === i ? null : i));
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      footer={
        <Button variant="primary" block onClick={onClose}>
          Zacznij tydzień →
        </Button>
      }
    >
      <div className="flex flex-col gap-3.5">
        {/* Hero */}
        <div className="flex flex-col items-center text-center pt-1 pb-1.5">
          <Mascot pose="idea" size={76} shadow />
          <h2 className="font-display text-[20px] font-semibold text-navy mt-1" style={{ letterSpacing: "-0.025em" }}>
            Zaplanujmy tydzień
          </h2>
          {childNames ? (
            <p className="text-[13px] text-muted mt-1">Razem z {childNames}</p>
          ) : null}
        </div>

        {/* Nawigacja tygodnia */}
        <div className="flex items-center justify-between -mb-1">
          <button
            type="button"
            onClick={() => setWeekOffset((o) => o - 1)}
            className="w-8 h-8 rounded-full border border-line bg-card text-muted hover:text-navy flex items-center justify-center"
            aria-label="Poprzedni tydzień"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => setWeekOffset(0)}
            className="text-[12px] font-semibold text-navy hover:text-green transition-colors"
            disabled={plan.isCurrentWeek}
          >
            {plan.isCurrentWeek ? (
              <span>Ten tydzień · {plan.weekLabel}</span>
            ) : (
              <span>{plan.weekLabel} · wróć do dziś</span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setWeekOffset((o) => o + 1)}
            className="w-8 h-8 rounded-full border border-line bg-card text-muted hover:text-navy flex items-center justify-center"
            aria-label="Następny tydzień"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Week strip — kliknij dzień żeby zobaczyć zaplanowane zadania
            i dorzucić nowe. Przeszłe dni wyszarzone i niedostępne. */}
        <div className="grid grid-cols-7 gap-1.5">
          {plan.days.map((d) => {
            const isSelected = selectedDayIndex === d.index;
            const base = "rounded-[12px] py-2 text-center transition-[transform,box-shadow]";

            // Hierarchia kolorów: past < default < today (akcent jasny) < selected (silna obramówka).
            // Today + selected: oba style się składają (jasne tło + ciemna obramówka).
            const cls = (() => {
              if (d.isPast) {
                return `${base} bg-cloud border border-line opacity-50 cursor-not-allowed`;
              }
              if (d.isToday && isSelected) {
                return `${base} active:translate-y-px bg-hay border-2 border-green`;
              }
              if (d.isToday) {
                return `${base} active:translate-y-px bg-hay border border-hay-bd`;
              }
              if (isSelected) {
                return `${base} active:translate-y-px bg-card border-2 border-green text-navy`;
              }
              return `${base} active:translate-y-px bg-card text-muted border border-line`;
            })();

            const labelCls = d.isPast
              ? "text-[11px] font-semibold text-muted"
              : d.isToday
                ? "text-[11px] font-semibold text-pending"
                : "text-[11px] font-semibold text-muted";

            const numCls = d.isPast
              ? "text-[16px] font-bold text-muted mt-0.5"
              : "text-[16px] font-bold text-navy mt-0.5";

            return (
              <button
                key={d.index}
                type="button"
                onClick={() => toggleDay(d.index)}
                className={cls}
                aria-pressed={isSelected}
                aria-disabled={d.isPast}
                disabled={d.isPast}
              >
                <div className={labelCls}>{d.short}</div>
                <div className={numCls}>{d.num}</div>
                {d.choreCount > 0 ? (
                  <div className="inline-block w-1.5 h-1.5 rounded-full bg-green mt-1" />
                ) : (
                  <div className="inline-block w-1.5 h-1.5 mt-1" />
                )}
              </button>
            );
          })}
        </div>

        {/* Expand panel wybranego dnia: lista zadań + przycisk dodaj */}
        {selectedDay ? (
          <DayPanel
            day={selectedDay}
            childByUid={new Map(allChildren.map((m) => [m.userId, m]))}
          />
        ) : null}

        {/* Cel tygodnia — MVP: stały hint, P3 dorzuci konfigurację */}
        <div
          className="rounded-[16px] border border-hay-bd px-4 py-3.5"
          style={{
            backgroundImage:
              "linear-gradient(135deg, rgba(255,201,60,0.18), rgba(255,201,60,0.05))",
          }}
        >
          <div className="text-[11px] font-bold uppercase tracking-[0.05em]" style={{ color: "#7A5A00" }}>
            ⭐ Pomysł na tydzień
          </div>
          <div className="font-display text-[17px] font-semibold text-navy mt-0.5" style={{ letterSpacing: "-0.02em" }}>
            Każde dziecko proponuje co najmniej 1 zadanie
          </div>
          <div className="text-[12px] text-muted mt-1">
            Ułóżcie razem dni, w które się sprawdzi
          </div>
        </div>

        {/* Propozycje od dzieci */}
        <div>
          <div className="text-[12px] font-bold uppercase tracking-[0.05em] text-muted mb-2">
            Propozycje od dzieci
          </div>

          {plan.proposals.length === 0 ? (
            <div className="rounded-[14px] border border-dashed border-line bg-card px-4 py-5 text-center">
              <div className="text-[13px] text-muted">
                Pusto na razie. Niech dzieci wrzucą swoje pomysły z zakładki Zadania.
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              {plan.proposals.map((p) => {
                const child = childByUid.get(p.proposedBy);
                const tone = child ? AVATAR_TONE[child.avatarColor] : "bg-purple";
                const initial = child?.displayName.charAt(0).toUpperCase() ?? "?";
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={goToApprovals}
                    className="w-full flex items-center gap-2.5 rounded-[14px] border border-line bg-card px-3 py-2.5 text-left transition-[transform] active:translate-y-px"
                  >
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-[12px] font-bold shrink-0 ${tone}`}
                    >
                      {initial}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-navy leading-tight truncate">
                        {p.title}
                      </div>
                      <div className="text-[11px] text-muted mt-0.5 truncate">
                        {child?.displayName ?? "Dziecko"}
                        {p.suggestedRewardMinor > 0 ? (
                          <> · {(p.suggestedRewardMinor / 100).toFixed(2).replace(".", ",")} zł</>
                        ) : (
                          <> · pomoc rodzinie</>
                        )}
                      </div>
                    </div>
                    <div
                      className="w-7 h-7 rounded-full bg-green text-white text-[18px] font-bold flex items-center justify-center shrink-0"
                      style={{ boxShadow: "0 2px 0 var(--kd-forest)" }}
                    >
                      +
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="text-center text-[12px] italic text-muted py-1">
          Otwórzcie to z dziećmi i ustalcie razem 💬
        </div>

        {/* Floating pill — sticky do dolnej krawędzi scroll containera,
            wycentrowany na prawą stronę. Krótki tekst "Dodaj zadanie" —
            kontekst (który dzień) jest już w ramce wybranego dnia powyżej. */}
        {selectedDay && !selectedDay.isPast ? (
          <div className="sticky bottom-3 z-10 ml-auto w-fit pointer-events-auto animate-in fade-in slide-in-from-bottom-2 duration-200">
            <button
              type="button"
              onClick={() => setQuickAddDate(selectedDay.date)}
              className="flex items-center gap-2 rounded-full bg-green text-white text-[13px] font-bold pl-3 pr-4 h-10 shadow-[0_4px_14px_-2px_rgba(58,114,69,0.55),0_2px_0_var(--kd-forest)] hover:bg-[color-mix(in_srgb,var(--kd-green)_92%,black)] active:translate-y-px transition-[transform,box-shadow]"
            >
              <Plus className="w-4 h-4" />
              Dodaj zadanie
            </button>
          </div>
        ) : null}
      </div>

      <QuickAddChoreSheet
        open={quickAddDate !== null}
        onClose={() => setQuickAddDate(null)}
        date={quickAddDate}
      />
    </Sheet>
  );
}

function DayPanel({
  day,
  childByUid,
}: {
  day: PlanDay;
  childByUid: Map<string, Member>;
}) {
  const dayName = format(day.date, "EEEE", { locale: pl });
  const dateLabel = format(day.date, "d MMMM", { locale: pl });

  return (
    <div className="rounded-[16px] border border-line bg-card p-3.5">
      <div className="flex items-baseline justify-between mb-2.5">
        <div>
          <div className="text-[15px] font-display font-semibold text-navy capitalize" style={{ letterSpacing: "-0.02em" }}>
            📅 {dayName}
          </div>
          <div className="text-[11px] text-muted">{dateLabel}</div>
        </div>
        <div className="text-[11px] text-muted font-num">
          {day.choreCount} {plLabel(day.choreCount)}
        </div>
      </div>

      {day.chores.length === 0 ? (
        <div className="text-center py-4 text-[12px] text-muted italic">
          Pusty dzień. Dorzucicie coś?
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          {day.chores.map((c) => (
            <ChoreRow key={c.id} chore={c} childByUid={childByUid} />
          ))}
        </div>
      )}
    </div>
  );
}

function ChoreRow({
  chore,
  childByUid,
}: {
  chore: Chore;
  childByUid: Map<string, Member>;
}) {
  const isGroupAssign =
    chore.assignedTo === CHORE_ASSIGN_ANY ||
    chore.assignedTo === CHORE_ASSIGN_ALL_CHILDREN;
  const child = isGroupAssign ? null : childByUid.get(chore.assignedTo);
  const assignedLabel =
    child?.displayName ??
    assignedToLabel(chore.assignedTo, []); // members[] puste — dla grup wystarczy
  const isFamily = chore.kind === "family";
  return (
    <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-[10px] bg-cloud">
      <span className="text-[16px] leading-none shrink-0">
        {chore.icon ?? (isFamily ? "🤝" : "💰")}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold text-navy leading-tight truncate">
          {chore.title}
        </div>
        <div className="text-[10px] text-muted mt-0.5 truncate">
          {assignedLabel}
          {!isFamily && chore.rewardMinor > 0 ? (
            <> · {(chore.rewardMinor / 100).toFixed(2).replace(".", ",")} zł</>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function plLabel(n: number): string {
  if (n === 1) return "zadanie";
  const last = n % 10;
  const lastTwo = n % 100;
  if (last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)) return "zadania";
  return "zadań";
}
